import React, { useMemo, useState } from "react";
import { Copy, Rss, CalendarPlus } from "lucide-react";
import { SPORTS, TEAMS } from "@/constants/categories";
import MultiSelect from "@/components/MultiSelect";
import { useCategories } from "@/context/CategoriesContext";
import { API } from "@/lib/api";
import { toast } from "sonner";

export default function IcsBuilder({ city = "worcester" }) {
  const { sports: allCats, teams: allOrgs, sportLabel } = useCategories();
  const [sports, setSports] = useState(SPORTS.map((s) => s.id));
  const [teams, setTeams] = useState(TEAMS.map((t) => t.id));

  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.set("city", city);
    if (sports.length && sports.length < allCats.length) params.set("sports", sports.join(","));
    if (teams.length && teams.length < allOrgs.length) params.set("teams", teams.join(","));
    return `${API}/ics?${params.toString()}`;
  }, [sports, teams, city, allCats.length, allOrgs.length]);

  const webcal = url.replace(/^https?:\/\//, "webcal://");

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Subscribe URL copied to clipboard");
  };

  return (
    <div className="bg-ec-surface border border-white/10 p-6 sm:p-8" data-testid="ics-builder">
      <div className="flex items-center gap-2.5 mb-1">
        <Rss className="text-ec-volt" size={20} />
        <h3 className="font-heading text-3xl tracking-wide leading-none">Build Your Calendar Feed</h3>
      </div>
      <p className="text-zinc-400 text-sm mb-6 max-w-2xl">
        Choose the categories and organisers you care about, then subscribe once — your calendar app
        stays updated automatically as new {city.charAt(0).toUpperCase() + city.slice(1)} events are added.
        Leave both on "All" to follow everything.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <MultiSelect
          label="Categories"
          testid="ics-categories"
          options={allCats}
          selected={sports}
          onChange={setSports}
        />
        <MultiSelect
          label="Organisers"
          testid="ics-organisers"
          options={allOrgs.map((t) => ({ ...t, group: t.sport, groupLabel: sportLabel(t.sport) }))}
          selected={teams}
          onChange={setTeams}
        />
      </div>

      <div className="border border-white/10 bg-black/40 p-4">
        <p className="overline text-zinc-500 mb-2">Your subscribe URL</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="ics-url"
            readOnly
            value={url}
            className="flex-1 bg-transparent border border-white/10 px-3 py-2.5 text-sm text-zinc-300 focus:outline-none focus:border-ec-volt font-mono truncate"
          />
          <div className="flex gap-2">
            <button
              data-testid="ics-copy-btn"
              onClick={copy}
              className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-4 py-2.5 hover:bg-ec-volt-hover transition-colors"
            >
              <Copy size={15} /> Copy
            </button>
            <a
              data-testid="ics-subscribe-btn"
              href={webcal}
              className="inline-flex items-center gap-2 overline border border-white/15 px-4 py-2.5 text-zinc-200 hover:border-ec-volt hover:text-ec-volt transition-colors"
            >
              <CalendarPlus size={15} /> Subscribe
            </a>
          </div>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid="ics-download-link"
          className="inline-block mt-3 text-xs text-zinc-500 hover:text-ec-volt underline"
        >
          or download the .ics file
        </a>
      </div>
    </div>
  );
}
